import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AISupportChat from './AISupportChat';
import './AISupportChat.css';

/**
 * AISupportWidget - Context-Aware Chatbot Widget
 *
 * Shows different chatbot modes based on the current page:
 * - sales: For marketing/pricing pages (lead generation focus)
 * - support: For dashboard/account pages (help & troubleshooting)
 * - wizard: For setup/configuration pages (step-by-step guidance)
 *
 * Automatically hides on pages where chatbot would be distracting:
 * - Broadcast Studio (live streaming)
 * - Every page a host's attendees or buyers see (registration, thank-you,
 *   watch/live/replay, landing, podcast, course pages)
 * - Login/Register pages
 * - Legal pages
 */

// Pages that should NOT show the chatbot
const EXCLUDED_PATHS = [
  '/broadcast-studio',
  '/studio',
  '/stage',
  '/backstage',
  '/guest-join',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/terms',
  '/privacy',
  '/data-processing',
  '/super-admin',
  '/feature-flags',
  '/monitoring',
  '/page-builder',
  '/ai-script-generator',
  '/ai-clip-generator',
  '/template-chooser',
  '/template-preview',
  '/video-library',
  '/library',
];

// Pattern-based exclusions
const EXCLUDED_PATTERNS = [
  /^\/broadcast-studio/,
  /^\/studio/,
  /^\/stage\//,
  /^\/backstage\//,
  /^\/guest/,
  /^\/webinar\/.*\/watch/,
  /^\/webinar\/.*\/live/,
  /^\/super-admin/,
  /^\/page-builder/,
  /^\/template-preview/,
  // Branded clip landing page (/clip/:uid) + recording watch page (/watch/:id)
  // — clean, distraction-free funnel surfaces (Nav + Footer hidden there too).
  // The Canira help bubble would leak onto a white-labeled page.
  /^\/clip\//,
  /^\/watch\//,
  // Every page a host's ATTENDEES or buyers see. The bubble is Canira's own
  // sales/support bot: on a host's registration form it pulls a visitor away
  // from the one thing the page is for, and it offers our pricing to the
  // host's audience. (`/register` above is the account sign-up page; a
  // webinar's `/register/<slug>` was never covered and showed the bot in
  // sales mode.)
  /^\/register\/./,           // webinar registration + thank-you screen
  /^\/webinars\/[^/]+\/register/, // the older registration page
  /^\/w\//,                   // host landing pages (page builder)
  // Not /webinar/:id/hub, which is the HOST's page.
  /^\/webinar\/[^/]+\/(watch|live|replay|purchase-success)(\/|$)/,
  /^\/webinars\/(?!manage$|create$)[^/]+$/, // a webinar's public page
  /^\/embed\//,
  /^\/join\//,                // guest join
  /^\/podcast\//,             // a host's public podcast page
  /^\/course\//,              // course pages, catalog and lessons
  /^\/creator\//,             // a host's public course list
  /^\/explore\/[^/]+/,        // a host's public profile
  /^\/book\/manage\//,        // reschedule a booking
  // Canira's own training sign-up: a registration form like any other.
  /^\/workshop(\/|$)/,
  /^\/coach-workshop(\/|$)/,
  /^\/cohort-1$/,
];

/** Whether the support bubble shows on this path. Exported for tests. */
export function supportWidgetShows(pathname) {
  if (EXCLUDED_PATHS.includes(pathname)) return false;
  return !EXCLUDED_PATTERNS.some((pattern) => pattern.test(pathname));
}

// Determine bot mode based on path
const getBotMode = (pathname) => {
  // Sales mode for marketing pages
  if (
    pathname === '/' ||
    pathname === '/home' ||
    pathname === '/pricing' ||
    pathname.startsWith('/features/')
  ) {
    return 'sales';
  }

  // Wizard mode for setup/configuration pages
  if (
    pathname.includes('/create') ||
    pathname.includes('/edit') ||
    pathname.includes('/settings') ||
    pathname === '/destinations' ||
    pathname === '/custom-domains' ||
    pathname === '/api-webhooks' ||
    pathname === '/team-settings' ||
    pathname === '/evergreen-settings' ||
    pathname === '/streaming-settings'
  ) {
    return 'wizard';
  }

  // Support mode (default for authenticated pages)
  return 'support';
};

// Get greeting based on mode
const getGreeting = (mode, userName) => {
  const name = userName ? ` ${userName}` : '';

  switch (mode) {
    case 'sales':
      return `Hi${name}! 👋 I'm here to help you learn about Canira. What would you like to know about our webinar platform?`;
    case 'wizard':
      return `Hello${name}! 🧙‍♂️ I'm your setup guide. I'll help you configure things correctly. What are you trying to set up?`;
    case 'support':
    default:
      return `Hi${name}! I'm your Canira AI assistant. I can help you with:\n\n- Creating and managing webinars\n- Technical troubleshooting\n- Feature explanations\n- Billing questions\n\nHow can I help you today?`;
  }
};

// Get quick suggestions based on mode
const getSuggestions = (mode) => {
  switch (mode) {
    case 'sales':
      return [
        { text: 'What features do you offer?', icon: '✨' },
        { text: 'How much does it cost?', icon: '💰' },
        { text: 'Can I get a demo?', icon: '🎬' },
        { text: 'How do automated webinars work?', icon: '🔄' },
      ];
    case 'wizard':
      return [
        { text: 'Help me create a webinar', icon: '📹' },
        { text: 'Set up streaming destinations', icon: '📡' },
        { text: 'Configure automated replay', icon: '⏰' },
        { text: 'Customize registration page', icon: '📝' },
      ];
    case 'support':
    default:
      return [
        { text: 'How do I go live?', icon: '🔴' },
        { text: 'Video upload issues', icon: '📤' },
        { text: 'Billing question', icon: '💳' },
        { text: 'Connect YouTube/Facebook', icon: '🔗' },
      ];
  }
};

const HIDDEN_STORAGE_KEY = 'canira_chat_hidden';

const AISupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  // Persisted "hide the launcher" state. Users asked for a way to get the
  // floating bubble off their screen entirely (distinct from just closing the
  // open chat window). We persist the choice so it survives reloads, and keep a
  // small restore handle so the assistant is never permanently unreachable.
  const [isHidden, setIsHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDDEN_STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  });
  const location = useLocation();

  const hideWidget = () => {
    setIsOpen(false);
    setIsHidden(true);
    try {
      localStorage.setItem(HIDDEN_STORAGE_KEY, '1');
    } catch (_) {
      /* storage unavailable — hide still applies for this session */
    }
  };

  const restoreWidget = () => {
    setIsHidden(false);
    try {
      localStorage.removeItem(HIDDEN_STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  // Determine if chatbot should be shown on this page
  const shouldShow = useMemo(() => supportWidgetShows(location.pathname), [location.pathname]);

  // Get bot mode for current page
  const botMode = useMemo(() => {
    return getBotMode(location.pathname);
  }, [location.pathname]);

  // Get appropriate suggestions for mode
  const suggestions = useMemo(() => {
    return getSuggestions(botMode);
  }, [botMode]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  // Don't render on excluded pages
  if (!shouldShow) {
    return null;
  }

  // Hidden by the user: show only a minimal, low-profile handle to bring the
  // assistant back. Keeps the screen clean without stranding them.
  if (isHidden) {
    return (
      <button
        className={`ai-chat-restore ${botMode}-mode`}
        onClick={restoreWidget}
        aria-label="Show the chat assistant"
        title="Show chat assistant"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
          <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
        </svg>
        <span>Help</span>
      </button>
    );
  }

  // Get icon based on mode
  const getButtonIcon = () => {
    if (isOpen) {
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      );
    }

    switch (botMode) {
      case 'sales':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            <path d="M11 12h2v2h-2zm0-6h2v5h-2z"/>
          </svg>
        );
      case 'wizard':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
          </svg>
        );
      case 'support':
      default:
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
          </svg>
        );
    }
  };

  // Get button color based on mode - using brand gold colors
  const getButtonStyle = () => {
    switch (botMode) {
      case 'sales':
        return { background: 'linear-gradient(135deg, #C9A962, #D4B978)' };
      case 'wizard':
        return { background: 'linear-gradient(135deg, #D4B978, #B8983F)' };
      case 'support':
      default:
        return { background: 'linear-gradient(135deg, #C9A962, #D4B978)' };
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        className={`ai-chat-toggle ${botMode}-mode`}
        onClick={toggleChat}
        aria-label={isOpen ? 'Close chat' : `Open ${botMode} chat`}
        style={getButtonStyle()}
      >
        {getButtonIcon()}
      </button>

      {/* Dismiss handle — hides the launcher entirely. Only shown while the
          chat window is closed (when open, the toggle itself is the ✕ that
          collapses it back to the bubble). */}
      {!isOpen && (
        <button
          className="ai-chat-dismiss"
          onClick={hideWidget}
          aria-label="Hide the chat assistant"
          title="Hide chat"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      )}

      {/* Chat Window */}
      <AISupportChat
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        botMode={botMode}
        customGreeting={getGreeting(botMode)}
        customSuggestions={suggestions}
        pageContext={location.pathname}
      />
    </>
  );
};

export default AISupportWidget;
